import { Backdrop, InputLabel, Paper, styled } from "@mui/material";
import React from "react";
import colors from "../../../theme/themeConstants";
import RoundButton from "../../buttons/RoundButton";
import cross from "components/icons/cross-black.svg";
import embedIcon from "../../icons/embed_desktop.svg";
import linkIcon from "../../icons/link_desktop.svg";
import RedditIcon from "../../icons/reddit_desktop.svg";
import TwitterIcon from "../../icons/twitter_desktop.svg";
import DiscordIcon from "../../icons/discord_desktop.svg";
import { ICollection, IGif } from "services/types";
import useWindowDimensions from "../../../hooks/useWindowDimension";
import { useDispatch } from "react-redux";
import { setServerResponse } from "store/application/application-action";
import { RedditShareButton, TwitterShareButton } from "react-share";
import { getSharedLink } from "../../../utils/share";

interface IProps {
  isOpen: boolean;
  togglePopup: () => void;
  gif?: IGif;
  collection?: ICollection;
  handleEmbedPopup?: () => void;
}

export const SharePopup = ({ isOpen, togglePopup, gif, collection, handleEmbedPopup }: IProps) => {

  const dispatch = useDispatch();

  const handleStopPropagation = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  const handleClose = (event: React.MouseEvent) => {
    console.debug("[Share] Close.");
    handleStopPropagation(event);
    togglePopup();
  };

  const isDesktop = useWindowDimensions();

  const handleCopyLinkButton = () => {
    if (gif) {
      navigator.clipboard.writeText(getSharedLink("gif", gif));
    } else if (collection) {
      navigator.clipboard.writeText(getSharedLink("collection", collection));
    }
    dispatch(setServerResponse("Link copied!"));
    togglePopup();
  };

  return (
    <Backdrop open={isOpen} id={"popupOpen"}
      sx={{
        zIndex: "var(--popupZ)",
      }}
      onClick={handleClose}
    >
      <Paper onClick={handleStopPropagation} sx={{
        height: "auto",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        maxWidth: "25rem",
        position: "absolute",
        bottom: isDesktop ? (isOpen ? "15rem" : "-15rem") : (isOpen ? "0rem" : "-15rem"),
        transition: "bottom 300ms cubic-bezier(0, 0, 1, 1) 0ms",
        margin: "0",
        borderRadius: "24px",
        borderBottomLeftRadius: "0px",
        borderBottomRightRadius: "0px",
        padding: "2rem 1rem 0 1rem",
        "@media screen and (max-width: 767px)": {
          borderRadius: 0,
          maxWidth: "unset",
          borderTopLeftRadius: "24px",
          borderTopRightRadius: "24px",
        }
      }} >
        {!isDesktop && (
          <MobileHandler
            onClick={togglePopup}
          />
        )}
        <FlexContainer>
          <h3 style={{ fontSize: "20px", fontWeight: 500, }}>Share:</h3>
          {isDesktop && <RoundButton img={cross} bgColor={"transparent"} onClick={handleClose} imgSize="28px" />}
        </FlexContainer>

        <SharingButtons>
          {gif && (
            <SharingButtonContainer onClick={handleEmbedPopup}>
              <img src={embedIcon} alt="Embed" />
              <ShareButtonLabel>Embed</ShareButtonLabel>
            </SharingButtonContainer>
          )}
          <SharingButtonContainer onClick={handleCopyLinkButton}>
            <img src={linkIcon} alt="Copy link" />
            <ShareButtonLabel>Copy link</ShareButtonLabel>
          </SharingButtonContainer>
        </SharingButtons>

        <h3 style={{ fontSize: "20px", fontWeight: 500, }}>Social Share:</h3>

        <SharingButtons>
          <RedditShareButton
            url={collection ? getSharedLink("collection", collection) : getSharedLink("gif", gif)}
            style={{ width: "100%" }}
          >
            <SharingButtonContainer>
              <img src={RedditIcon} alt="Reddit" />
              <ShareButtonLabel>Reddit</ShareButtonLabel>
            </SharingButtonContainer>
          </RedditShareButton>
          <TwitterShareButton
            url={collection ? getSharedLink("collection", collection) : getSharedLink("gif", gif)}
            style={{ width: "100%" }}
          >
            <SharingButtonContainer>
              <img src={TwitterIcon} alt="Twitter" />
              <ShareButtonLabel>Twitter</ShareButtonLabel>
            </SharingButtonContainer>
          </TwitterShareButton>
          <SharingButtonContainer onClick={handleCopyLinkButton}>
            <img src={DiscordIcon} alt="Discord" />
            <ShareButtonLabel>Discord</ShareButtonLabel>
          </SharingButtonContainer>
        </SharingButtons>
      </Paper>
    </Backdrop>
  );
};

const FlexContainer = styled("div")({
  display: "flex",
  justifyContent: "space-between",
  width: "100%"
});

const ShareButtonLabel = styled(InputLabel)({
  fontSize: "16px",
  fontFamily: "Poppins",
  fontWeight: 500,
  cursor: "pointer"
});

const SharingButtonContainer = styled("div")({
  width: "100%",
  backgroundColor: colors.lightGrayPaperBackground,
  backgroundVisibility: 0.3,
  borderRadius: "12px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "1rem 0.5rem",
  gap: "0.5rem",
  cursor: "pointer",
});

const SharingButtons = styled("div")({
  display: "flex",
  margin: "1.5rem 0 2rem",
  justifyContent: "space-between",
  gap: "1rem"
});

const MobileHandler = styled("div")({
  background: "rgba(216, 223, 236, 0.7)",
  borderRadius: "12px",
  width: "32px",
  height: "4px",
  alignSelf: "center",
  marginBottom: "1rem"
});
